import { Entity } from './base-raptor-models';

/**
 * Which inspection a skip reason is for: `General` for regular inspections, `QC` for handbacks.
 * Mirrors the backend enum, sent as its name.
 */
export enum InspectionSkipReasonType {
    General = 'General',
    QC = 'QC',
}

/**
 * Tenant-curated reason a technician selects when skipping a Digital Vehicle Inspection instead of
 * completing it item-by-item. Mirrors the backend `InspectionSkipReasonOutputMessage` / `IInspectionSkipReason`
 * contract. Extends `Entity` so multi-tenant identity (`id`, `tenantId`, `branchId`) and audit fields
 * (`createdBy`, `createdDate`, `modifiedBy`, `modifiedDate`) are part of the shape.
 *
 * Reasons are tenant-level by default (`branchId` null applies the reason across all branches) but may
 * be scoped to a single branch by setting `branchId`. Retired reasons are marked `isActive = false`
 * rather than deleted so inspections skipped against them retain a resolvable reference.
 */
export interface InspectionSkipReason extends Entity {
    /**
     * Stable machine code identifying the reason within the tenant. Persisted on a skipped
     * inspection's skip-reason reference, so it must remain stable once referenced.
     */
    code: string;

    /**
     * Human-readable label shown to the technician when choosing a skip reason.
     */
    name: string;

    /**
     * Whether this reason is currently selectable. Inactive reasons are retained for historical
     * integrity of already-skipped inspections but are not offered for new skips.
     */
    isActive: boolean;

    /**
     * Which inspection the reason is for. The server already filters by it, so clients don't.
     */
    type: InspectionSkipReasonType;

    /**
     * True when the reason was synced from the federation; read-only in admin. Set by the server only.
     */
    isFederationManaged?: boolean;
}
